import { useEffect, useState } from "react";
import type { ForageKind, ForagePlant as ForagePlantData } from "../api/forage";
import { KIND_GREEN_PNG, KIND_RED_PNG, SPRITE_BIAS } from "./forageSprites";
import { E32 } from "../theme/endesga32";

// How long the green→red overlay fade takes once the server marks a
// plant ripe. Cosmetic only — server-decided harvestability uses the
// boolean `state` field; this controls the visual tween.
export const RIPEN_FADE_MS = 10_000;

// Compute the red-overlay opacity from the server's `red_at`
// timestamp. Returns `opacity` (current) and `remainingMs` (how much
// transition time is left). Recomputing per RAF tick — rather than
// kicking off a CSS transition once on prop change — is what lets a
// background-tab return snap straight to the truthful opacity
// instead of replaying a stale animation.
function computeRipen(plant: ForagePlantData): {
  opacity: number;
  remainingMs: number;
} {
  if (plant.state !== "red" || plant.red_at == null) {
    return { opacity: plant.state === "green" ? 0 : 1, remainingMs: 0 };
  }
  // Frenzy-ripened apples skip the fade entirely — straight to full red.
  if (plant.instant) return { opacity: 1, remainingMs: 0 };
  const elapsed = Date.now() - plant.red_at;
  if (elapsed >= RIPEN_FADE_MS) return { opacity: 1, remainingMs: 0 };
  if (elapsed <= 0) return { opacity: 0, remainingMs: RIPEN_FADE_MS };
  return {
    opacity: elapsed / RIPEN_FADE_MS,
    remainingMs: RIPEN_FADE_MS - elapsed,
  };
}

interface Props {
  plant: ForagePlantData;
  kind: ForageKind;
  containerW: number;
  containerH: number;
  hidden: boolean;
  onClick: (
    slot: ForagePlantData,
    left: number,
    top: number,
    cx: number,
    cy: number,
    clientX: number,
    clientY: number,
  ) => void;
  /** Keyboard cursor lit on this plant. Paints a focus ring around
   *  the slot so a tab-only user can see what Enter will harvest. */
  cursor?: boolean;
}

// One plant slot in the orchard. Layered images: the green PNG is
// the base; the red PNG sits on top with opacity transitioning from
// 0 to 1 over `RIPEN_FADE_MS` when the server flips this plant from
// green to red. The DOM element does NOT remount across state
// changes — same id, same wrapper, same position/rotation — so the
// only visual change during ripening is the red overlay's opacity.
export function ForagePlant({
  plant,
  kind,
  containerW,
  containerH,
  hidden,
  onClick,
  cursor = false,
}: Props) {
  const left = plant.x_frac * containerW - plant.size / 2;
  const top = plant.y_frac * containerH - plant.size / 2;
  const cx = left + plant.size / 2;
  const cy = top;

  const [redOpacity, setRedOpacity] = useState<number>(
    () => computeRipen(plant).opacity,
  );

  useEffect(() => {
    if (plant.state === "green") {
      setRedOpacity(0);
      return;
    }
    if (plant.state === "falling") {
      setRedOpacity(1);
      return;
    }
    // state === "red": run a RAF loop until opacity hits 1. Re-tick
    // on visibilitychange so a returning background tab catches up to
    // wall-clock truth on its very first visible frame, instead of
    // resuming a stale CSS transition where it left off.
    let raf: number | null = null;
    const tick = () => {
      const { opacity } = computeRipen(plant);
      setRedOpacity(opacity);
      if (opacity < 1) raf = requestAnimationFrame(tick);
    };
    tick();

    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [plant.state, plant.red_at, plant.id]);

  const isRed = plant.state === "red";
  const isFalling = plant.state === "falling";
  const [hover, setHover] = useState(false);

  // CSS animation by state. Applied to an inner wrapper rather than
  // the outer wrapper. Both ripe-pulse (transform: scale) and
  // apple-fall (transform: translate) force the animating element
  // onto its own compositor layer. Keeping that layer on the INNER
  // wrapper means the outer wrapper — which carries left/top/zIndex
  // — stays on the main compositor layer where z-index ordering
  // against its siblings is honoured reliably. (See the page-level
  // greens/reds/falling container split too.)
  const animation = isFalling
    ? "apple-fall 1500ms cubic-bezier(.45,.05,.55,.95) forwards"
    : isRed
      ? "ripe-pulse 1600ms cubic-bezier(.2,.9,.2,1) infinite"
      : undefined;

  // Red plants can be harvested; green plants can be clicked too —
  // the page-level handler routes a green click to a "not ripe yet"
  // notice sound. Falling and hidden (mid-harvest) plants ignore
  // clicks entirely.
  const isHarvestable = isRed && !hidden;
  const acceptsClick = !isFalling && !hidden;

  // Per-plant z-index lives WITHIN the page-level greens/reds
  // container — it only orders this plant against its same-state
  // neighbours. Green/red separation is enforced structurally by the
  // page-level container split.
  const zIndex = hover ? 99 : plant.z;

  return (
    <div
      onClick={(e) => {
        if (!acceptsClick) return;
        e.stopPropagation();
        onClick(plant, left, top, cx, cy, e.clientX, e.clientY);
      }}
      onMouseEnter={() => isHarvestable && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "absolute",
        left,
        top,
        width: plant.size,
        height: plant.size,
        zIndex: cursor ? 250 : zIndex,
        cursor: isHarvestable ? "pointer" : "default",
        userSelect: "none",
        WebkitUserSelect: "none",
        pointerEvents: hidden || isFalling ? "none" : "auto",
        // Hide the slot while a harvest-pop ghost is animating in its
        // place. visibility (rather than display: none) keeps the
        // wrapper laid out so animation state isn't reset.
        visibility: hidden ? "hidden" : "visible",
        // Keyboard cursor: paint a 3px ink outline (palette-only) that
        // sits above all sprites. Box-shadow over `outline` because
        // the inner div is the one carrying transforms/animation.
        boxShadow: cursor ? `0 0 0 3px ${E32.ink}` : undefined,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `rotate(${plant.rotation}deg)`,
          animation,
        }}
      >
        <img
          src={KIND_GREEN_PNG[kind]}
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            imageRendering: "pixelated",
            pointerEvents: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            transform: SPRITE_BIAS[kind],
          }}
        />
        <img
          src={KIND_RED_PNG[kind]}
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            imageRendering: "pixelated",
            opacity: redOpacity,
            // Opacity is set per frame from `red_at`; no CSS transition.
            transition: "none",
            pointerEvents: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            transform: SPRITE_BIAS[kind],
          }}
        />
      </div>
    </div>
  );
}
